import axios from 'axios';
import { ENDPOINTS } from '@/settings/endpoints';
import { CourseCertificateInfo } from '@/models/studentDocuments';
import SentryClient from '../sentry';
import Analytics from '../analytics';
import { EVENT_LABELS } from '../analytics/labels';
import { CourseModel } from '@descomplica/dex-models';
import { getChimeraToken } from '../get-tokens';
import { CertificateEligibilityInfo, StudentDocumentsStatusInfo } from './interfaces';
import { PEGASUS_MUSTANG_STUDENTS_DOCUMENTS_NAMESPACE } from '@/settings/constants';

interface GetStudentDocumentsStatusResponseType {
  data: StudentDocumentsStatusInfo | null;
}

interface GetCertificateInfoResponseType {
  data: CourseCertificateInfo;
}

interface GetCertificateEligibilityResponseType {
  data: CertificateEligibilityInfo;
}

interface SSOOptions {
  redirectToDocumentsPage?: boolean;
  descomplicaCourseId?: string | null;
  idOrg?: string | null;
}

export default class StudentDocumentRequestService {
  static async getStudentDocumentsStatus(
    token: string,
    course: CourseModel,
  ): Promise<StudentDocumentsStatusInfo | null> {
    try {
      const response = await axios.get<GetStudentDocumentsStatusResponseType>(
        `${PEGASUS_MUSTANG_STUDENTS_DOCUMENTS_NAMESPACE}/status`,
        {
          baseURL: ENDPOINTS.pegasusMustang as string,
          params: {
            descomplicaCourseId: course.descomplicaCourseId,
            dexCourseId: course.id,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data.data;
    } catch (error: any) {
      SentryClient.captureError(error);
      Analytics.trackEvent(EVENT_LABELS.postgradStudentDocumentsError, {
        course: course.name,
        category: course.descomplicaCategorySlug,
      });
      return null;
    }
  }

  static getSSOEndpoint({
    redirectToDocumentsPage = false,
    descomplicaCourseId = '',
    idOrg = '',
  }: SSOOptions = {}): string {
    const url = new URL(
      `${ENDPOINTS.pegasusMustang}/${PEGASUS_MUSTANG_STUDENTS_DOCUMENTS_NAMESPACE}/erp-sso-redirect`,
    );
    url.searchParams.set('d', getChimeraToken());

    if (!redirectToDocumentsPage) url.searchParams.set('redirectToHomePage', '1');
    if (descomplicaCourseId) url.searchParams.set('descomplicaCourseId', descomplicaCourseId);
    if (idOrg) url.searchParams.set('idOrg', idOrg);

    return url.toString();
  }

  static executeSSO(options: SSOOptions = {}): void {
    window.open(this.getSSOEndpoint(options), '_blank');
  }

  static async getCertificateInfo(
    token: string,
    course: CourseModel,
  ): Promise<CourseCertificateInfo | null> {
    try {
      const response = await axios.get<GetCertificateInfoResponseType>(
        `${PEGASUS_MUSTANG_STUDENTS_DOCUMENTS_NAMESPACE}/certificate`,
        {
          baseURL: ENDPOINTS.pegasusMustang as string,
          params: {
            descomplicaCourseId: course.descomplicaCourseId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data.data;
    } catch (error: any) {
      if (error.response.status >= 500) {
        SentryClient.captureError(error);
      }

      Analytics.trackEvent(EVENT_LABELS.postgradStudentCertificateError, {
        student_certificate: true,
        course: course.name,
        category: course.descomplicaCategorySlug,
        error: error.message,
        error_identifier: error.response.data.identifier,
      });
      return null;
    }
  }

  static async getCertificateEligibility(
    token: string,
    descomplicaToken: string,
    course: CourseModel,
  ): Promise<CertificateEligibilityInfo | null> {
    try {
      if (!course.descomplicaCourseId) {
        return null;
      }

      const response = await axios.get<GetCertificateEligibilityResponseType>(
        `${PEGASUS_MUSTANG_STUDENTS_DOCUMENTS_NAMESPACE}/certificate/eligibility`,
        {
          baseURL: ENDPOINTS.pegasusMustang as string,
          params: {
            descomplicaCourseId: course.descomplicaCourseId,
            dexCourseId: course.id,
          },
          headers: {
            Authorization: `Bearer ${descomplicaToken}`,
            'Authorization-dex': `Bearer ${token}`,
          },
        },
      );

      return response.data.data;
    } catch (error: any) {
      if (error.response.status >= 500) {
        SentryClient.captureError(error);
      }
      return null;
    }
  }
}
